import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Pagination,
  Chip,
  Fab,
  Paper,
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  Add,
  LocationOn,
  Visibility,
  Category,
  Close,
  Dashboard,
  List,
} from '@mui/icons-material';
import ServiceAuth from '../../components/ServiceAuth';
import api from '../../services/api';
import { storeServiceAuth } from '../../utils/serviceAuth';

interface Listing {
  id: string;
  title: string;
  description: string;
  price: number;
  category: string;
  category_slug?: string;
  category_id: number;
  thumbnail_url: string;
  images: string[];
  location: string;
  status: string;
  coming_soon: boolean;
  views: number;
  created_at: string;
  seller_name: string;
}

interface Category {
  id: number;
  name: string;
  slug: string;
  icon: string;
  translation_key?: string;
}

const MarketplaceServicePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isServiceAuthenticated, setIsServiceAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [listings, setListings] = useState<Listing[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | ''>('');
  const [sortBy, setSortBy] = useState('newest');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    // Check if we have service authentication
    const serviceToken = localStorage.getItem('serviceToken');
    
    if (serviceToken) {
      setIsServiceAuthenticated(true);
    }
    
    // Always fetch categories and listings on mount
    fetchCategories();
    fetchListings();
  }, []);
  
  useEffect(() => {
    // Re-fetch listings when filters change
    fetchListings();
  }, [page, selectedCategory, sortBy]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/marketplace/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params: any = {
        page,
        per_page: 12,
        sort_by: sortBy,
        status: 'active',
      };

      if (searchTerm) params.search = searchTerm;
      if (selectedCategory) params.category_id = selectedCategory;
      if (priceRange.min) params.min_price = priceRange.min;
      if (priceRange.max) params.max_price = priceRange.max;

      const response = await api.get('/marketplace/listings', { params });
      setListings(response.data.listings);
      setTotalPages(response.data.pagination.pages);
    } catch (error) {
      console.error('Error fetching listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchListings();
  };

  const handleViewListing = (id: string) => {
    navigate(`/app/marketplace/listing/${id}`);
  };

  const handleCreateListing = () => {
    if (isServiceAuthenticated) {
      navigate('/app/marketplace/create');
    } else {
      setShowAuthModal(true);
    }
  };

  const handleAuthenticated = (userData: any) => {
    console.log('MarketplaceServicePage handleAuthenticated - userData:', userData);
    
    // Use utility function to store auth data
    if (userData.token) {
      storeServiceAuth(userData);
    }
    
    setIsServiceAuthenticated(true);
    setShowAuthModal(false);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  const getPlaceholderImage = () => {
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHRleHQtYW5jaG9yPSJtaWRkbGUiIHg9IjE1MCIgeT0iMTUwIiBzdHlsZT0iZmlsbDojYWFhO2ZvbnQtd2VpZ2h0OmJvbGQ7Zm9udC1zaXplOjE5cHg7Zm9udC1mYW1pbHk6QXJpYWwsSGVsdmV0aWNhLHNhbnMtc2VyaWY7ZG9taW5hbnQtYmFzZWxpbmU6Y2VudHJhbCI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {t('marketplace.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('marketplace.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          {isServiceAuthenticated && (
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Dashboard />}
                onClick={() => navigate('/app/marketplace/dashboard')}
              >
                {t('marketplace.dashboard.title')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<List />}
                onClick={() => navigate('/app/marketplace/my-listings')}
              >
                {t('marketplace.myListings.title')}
              </Button>
            </>
          )}
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateListing}
            color="secondary"
          >
            {t('marketplace.createListing')}
          </Button>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
          <TextField
            sx={{ flex: { md: 2 } }}
            fullWidth
            placeholder={t('marketplace.search.placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }
            }}
          />
          <FormControl sx={{ flex: 1 }} fullWidth>
            <InputLabel>{t('marketplace.search.category')}</InputLabel>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as number | '')}
              label={t('marketplace.search.category')}
            >
              <MenuItem value="">{t('marketplace.search.allCategories')}</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.slug ? t(`marketplace.categories.${cat.slug}`, { defaultValue: cat.name }) : cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            sx={{ flex: 1 }}
            fullWidth
            label={t('marketplace.search.minPrice')}
            type="number"
            value={priceRange.min}
            onChange={(e) => setPriceRange({ ...priceRange, min: e.target.value })}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
              }
            }}
          />
          <TextField
            sx={{ flex: 1 }}
            fullWidth
            label={t('marketplace.search.maxPrice')}
            type="number"
            value={priceRange.max}
            onChange={(e) => setPriceRange({ ...priceRange, max: e.target.value })}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
              }
            }}
          />
          <FormControl sx={{ flex: 1 }} fullWidth>
            <InputLabel>{t('marketplace.search.sortBy')}</InputLabel>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              label={t('marketplace.search.sortBy')}
            >
              <MenuItem value="newest">{t('marketplace.search.newest')}</MenuItem>
              <MenuItem value="price_asc">{t('marketplace.search.priceAsc')}</MenuItem>
              <MenuItem value="price_desc">{t('marketplace.search.priceDesc')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleSearch} startIcon={<Search />}>
            {t('marketplace.search.button')}
          </Button>
        </Box>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Listings Grid */}
      {!loading && (
        <>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(3, 1fr)' 
            }, 
            gap: 3 
          }}>
            {listings.map((listing) => (
              <Card key={listing.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardMedia
                  component="img"
                  height="200"
                  image={listing.thumbnail_url || getPlaceholderImage()}
                  alt={listing.title}
                  sx={{ 
                    objectFit: 'cover',
                    filter: listing.coming_soon ? 'grayscale(0.5)' : 'none',
                    opacity: listing.coming_soon ? 0.8 : 1,
                  }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography gutterBottom variant="h6" component="h2">
                      {listing.title}
                    </Typography>
                    {listing.coming_soon && (
                      <Chip
                        label={t('marketplace.listing.comingSoon')}
                        size="small"
                        color="warning"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </Box>
                  <Typography variant="h5" color="primary" gutterBottom>
                    {listing.coming_soon ? t('marketplace.listing.priceOnRequest') : formatPrice(listing.price)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    {listing.category && (
                      <Chip
                        icon={<Category />}
                        label={listing.category_slug ? t(`marketplace.categories.${listing.category_slug}`, { defaultValue: listing.category }) : listing.category}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {listing.location && (
                      <Chip
                        icon={<LocationOn />}
                        label={listing.location}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {listing.description}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Visibility fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="caption" color="text.secondary">
                      {listing.views} {t('marketplace.listing.views')}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => handleViewListing(listing.id)}
                  >
                    {t('marketplace.listing.viewDetails')}
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Box>

          {/* Empty State */}
          {listings.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                {t('marketplace.listing.noListings')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('marketplace.listing.adjustFilters')}
              </Typography>
            </Box>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* FAB for creating listing */}
      <Fab
        color="secondary"
        aria-label="add listing"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleCreateListing}
      >
        <Add />
      </Fab>

      {/* Authentication Modal */}
      <Dialog 
        open={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ position: 'relative', pt: 4 }}>
          <IconButton
            onClick={() => setShowAuthModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
          <ServiceAuth 
            onAuthenticated={handleAuthenticated}
            intent="marketplace"
          />
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default MarketplaceServicePage;